import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";
import { useCallback, useEffect, useState } from "react";
import {
  addOrganizationDocumentation,
  deleteOrganizationDocumentation,
  getOrganizationDocumentations,
  updateOrganizationDocumentation,
} from "../../../../../setup/api/services/organization";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import { IOrganizationDocumentation } from "../../../../../setup/api/services/organization/models";
import DenyAccess from "../../../../../components/denyAccess";
import { uploadFileWithPresignedUrl } from "../../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../../s3Integration/enums";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DocumentationUploadModal from "./components/DocumentationUploadModal";
import PrimaryButton, {
  PrimaryButtonSize,
} from "../../../../../components/common/PrimaryButton/PrimaryButton";
import PDFComponent from "../../../../../components/common/PDFComponent/PDFComponent";

interface DocumentationInformationProps {
  organization: IOrganizations | undefined;
}

const DocumentationInformation = ({
  organization,
}: DocumentationInformationProps) => {
  const { t } = useTranslation();
  const [documentations, setDocumentations] = useState<
    IOrganizationDocumentation[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [slotLoading, setSlotLoading] = useState<{ [key: string]: boolean }>(
    {}
  );

  const fetchDocumentations = useCallback(async () => {
    if (!organization) return;
    setIsLoading(true);
    try {
      const response = await getOrganizationDocumentations(organization.code);
      setDocumentations(response.data);
    } catch (error: any) {
      console.error("Error fetching documentation information:", error);
    } finally {
      setIsLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchDocumentations();
  }, [fetchDocumentations]);

  const handleFileUpload = useCallback(
    async (
      file: File,
      titleKey: string,
      existingDoc?: IOrganizationDocumentation
    ) => {
      if (!organization) return;

      setSlotLoading((prev) => ({ ...prev, [titleKey]: true }));
      try {
        const uploadResult = await uploadFileWithPresignedUrl(
          file,
          EUploadImageType.ORGANIZATION,
          organization?.code
        );

        const body = {
          title: t(titleKey),
          bucketName: uploadResult.bucket,
          objectName: uploadResult.key,
          description: file.name,
        };

        if (existingDoc) {
          await updateOrganizationDocumentation(
            organization.code,
            existingDoc.id,
            body
          );
        } else {
          await addOrganizationDocumentation(organization.code, body);
        }

        toast.success(t("documentation_saved_successfully"));
        await fetchDocumentations();
      } catch (error) {
        console.error(`Error uploading ${titleKey}:`, error);
        toast.error(t("documentation_save_error"));
      } finally {
        setSlotLoading((prev) => ({ ...prev, [titleKey]: false }));
      }
    },
    [organization, t, fetchDocumentations]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      if (!organization) return;
      setIsLoading(true);
      try {
        await deleteOrganizationDocumentation(organization.code, id);
        toast.success(t("documentation_deleted_successfully"));
        await fetchDocumentations();
      } catch (error) {
        console.error("Error deleting documentation:", error);
        toast.error(t("documentation_delete_error"));
      } finally {
        setIsLoading(false);
      }
    },
    [organization, t, fetchDocumentations]
  );

  const renderSlot = (doc: IOrganizationDocumentation) => {
    const isLoadingSlot = slotLoading[doc.title];
    const isPDF = doc?.attachmentUrl?.toLowerCase().includes(".pdf");

    return (
      <div className={styles.uploaded_image_container}>
        {isLoadingSlot && (
          <div className={styles.loadingOverlay}>
            <Spinner />
          </div>
        )}
        {isPDF ? (
          <PDFComponent
            fileUrl={doc.attachmentUrl}
            fileName={doc.description}
            isPreview
          />
        ) : (
          <img
            className={styles.uploaded_image}
            src={doc.attachmentUrl}
            alt={doc.title}
          />
        )}
        <div className={styles.action_icons}>
          <div className={styles.icon_wrapper}>
            <img src="/assets/images/svg/edit.svg" alt="edit" />
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file, doc.title, doc);
              }}
              accept="image/*, application/pdf"
            />
          </div>
          <div
            className={styles.icon_wrapper}
            onClick={() => handleDelete(doc.id)}>
            <img src="/assets/images/svg/delete_logo.svg" alt="delete" />
          </div>
        </div>
      </div>
    );
  };

  if (!organization && !isLoading) {
    return <DenyAccess />;
  }

  return (
    <div className={styles.container}>
      <ToastContainer />
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <p className={styles.title}>{t("lbl_upload_documentation")}</p>
        </div>
        <PrimaryButton
          onClick={() => setIsModalVisible(true)}
          width="fit-content"
          size={PrimaryButtonSize.MEDIUM}>
          {t("lbl_add_documentation")}
        </PrimaryButton>
      </div>

      {isLoading ? (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.contentContainer}>
          {documentations.map((doc) => (
            <div key={doc.id} className={styles.documentationItem}>
              <p className={styles.documentationTitle}>{doc.title}</p>
              {renderSlot(doc)}
            </div>
          ))}
        </div>
      )}

      {isModalVisible && (
        <DocumentationUploadModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={() => {
            setIsModalVisible(false);
            fetchDocumentations();
          }}
          organizationCode={organization?.code || ""}
        />
      )}
    </div>
  );
};

export default DocumentationInformation;
